/*
 * Phonebook
 *
 * - Holds the current set of phonebook contacts
 * - Holds the phonebook groups the user can see: the customer groups and the user's own personal groups
 */

import { computed, ref, reactive } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useRealtimeStore } from '@/stores/Realtime'
import { useApiStore } from '@/stores/Api'

import logger from '@/utilities/Logger'
import globalEvents from '@/utilities/GlobalEvents'

import type { ConfigEventCommon, ConfigEventPbxPhonebookCreated, ConfigEventPbxPhonebookDeleteAll, ConfigEventPbxPhonebookUpdated, ConfigEventPbxPhonebookGroupCreated, ConfigEventPbxPhonebookGroupUpdated, ConfigEventPbxPhonebookGroupDeleted } from '@per_moeller/tcx-realtime-client'
import { type PbxPhonebookContact, type PbxPhonebookCreateResponse, type PbxPhonebookCreateRequest, type PbxPhonebookUpdateRequest, type PbxPhonebookUpdateResponse, type PbxPhonebookDeleteResponse, type PbxPhonebookListResponse, type PbxPhonebookResponse, PbxPhonebookContactType } from '@/TcxApi/PbxPhonebook'
import type { PbxPhonebookGroup, PbxPhonebookGroupCreateRequest, PbxPhonebookGroupCreateResponse, PbxPhonebookGroupDeleteResponse, PbxPhonebookGroupListResponse, PbxPhonebookGroupResponse, PbxPhonebookGroupUpdateRequest, PbxPhonebookGroupUpdateResponse } from '@/TcxApi/PbxPhonebookGroup'
import { EmployeeAccessLevel } from '@/TcxApi/Employee'
import { dynamicSort, getDeepValue, sleep } from '@/utilities/Tools'
import { contactGroupIds, groupCounts as countGroups, groupNames, groupsForKind, sortGroups, stripGroup, upsertGroup } from '@/utilities/phonebookGroups'

export const usePhonebookStore = defineStore('phonebook', () => {
	logger.debug('Phonebook: Initializing store')

	const api = useApiStore()
	const realtime = useRealtimeStore()
	const ss = useSettingsStore()

	// #region Data

	const contacts = ref<PbxPhonebookContact[]>([])
	const _ready = ref(false)
	let isLoading = false

	const groups = ref<PbxPhonebookGroup[]>([])
	const _groupsLoaded = ref(false)

	/** The known groups by id */
	const groupsById = computed(() => new Map(groups.value.map(group => [group._id, group])))

	/** Number of groups to ask for per request - the API's max */
	const GROUP_PAGE_SIZE = 500

	// #endregion

	// #region Initializing, loading data

	init(true)

	async function init(initial = false) {
		await sleep(1)

		if (initial) {
			realtime.configEvents.on('PBX_PHONEBOOK_CREATED', createdEvent)
			realtime.configEvents.on('PBX_PHONEBOOK_UPDATED', updatedEvent)
			realtime.configEvents.on('PBX_PHONEBOOK_DELETED', deletedEvent)
			realtime.configEvents.on('PBX_PHONEBOOK_DELETE_ALL', deleteAllEvent)
			realtime.configEvents.on('PBX_PHONEBOOK_GROUP_CREATED', groupChangedEvent)
			realtime.configEvents.on('PBX_PHONEBOOK_GROUP_UPDATED', groupChangedEvent)
			realtime.configEvents.on('PBX_PHONEBOOK_GROUP_DELETED', groupDeletedEvent)
			realtime.realtimeEvents.on('Connected', init)
			globalEvents.on('Auth:LoggedOut', () => {
				contacts.value = []
				groups.value = []
				_groupsLoaded.value = false
			})
			globalEvents.on('StatusRequest', () => {
				globalEvents.emit('StatusReport', { key: 'phonebook', value: _ready.value })
			})
		}

		try {
			_ready.value = false
			await ss.ready()
			await loadContacts()
		}
		catch (err) {
			logger.error(`Phonebook: Error initializing: ${(err as Error).message}`)
			isLoading = false
		}
	}

	/**
	 * Build the text the phonebook search matches against - includes the names of the contact's groups
	 * @param contact Contact to build it for
	 */
	function buildSearch(contact: PbxPhonebookContact) {
		const search = ['givenName', 'familyName', 'company', 'title', 'department']
			.map(path => getDeepValue<PbxPhonebookContact, string>(contact, path))
			.filter(o => o)
			.map(o => o?.trim().toLowerCase())

		for (const address of contact.addresses) {
			search.push(['street', 'zip', 'state', 'city'].map(path => address[path as keyof typeof address]).filter(o => o).map(o => o?.trim().toLowerCase()).join(' '))
		}

		for (const address of contact.emailAddresses) {
			search.push(address.address)
		}

		for (const number of contact.phoneNumbers) {
			search.push(number.number)
		}

		for (const name of groupNames(contact.groups, groupsById.value)) {
			search.push(name.toLowerCase())
		}

		contact.search = search.join('   ')
	}

	function makeSearchAndStuff(contact: PbxPhonebookContact) {
		contact.groups = contactGroupIds(contact)
		buildSearch(contact)
		contact.company = contact.company?.trim() || null
		contact.familyName = contact.familyName?.trim() || null
		contact.givenName = contact.givenName?.trim() || null
		contact.name = contact.type == 'PERSON' ? [contact.givenName, contact.familyName].filter(o => o).join(' ') : contact.company
		if (!contact.name) { contact.name = contact.company }

		for (const number of contact.phoneNumbers) {
			number.number = ss.cutNationalPrefix(number.number)
		}
	}

	async function loadContacts() {
		_ready.value = false
		if (isLoading) { return }
		isLoading = true
		contacts.value = []

		const [res, loadedGroups] = await Promise.all([
			api.get<PbxPhonebookListResponse>('/pbx/phonebook?offset=0&limit=50000', { timeout: 30000 }),
			loadGroups()
		])

		if (loadedGroups) { // The groups first, so the contacts' search text gets their names
			groups.value = loadedGroups
			_groupsLoaded.value = true
		}

		for (const contact of res.data.contacts) {
			makeSearchAndStuff(contact)
		}

		res.data.contacts.sort(dynamicSort('name'))

		for (const contact of res.data.contacts) {
			contacts.value.push(reactive(contact))
		}
		_ready.value = true
		isLoading = false
	}

	/**
	 * Load the customer groups and the user's own personal groups
	 * @returns The groups sorted by name, undefined if they could not be loaded - the phonebook works without them
	 */
	async function loadGroups() {
		try {
			const loaded: PbxPhonebookGroup[] = []
			let total = 0

			do {
				const res = await api.get<PbxPhonebookGroupListResponse>(`/pbx/phonebookgroup?type=ALL&offset=${loaded.length}&limit=${GROUP_PAGE_SIZE}`)

				loaded.push(...res.data.groups)
				total = res.data.total
				if (res.data.groups.length === 0) { break }
			} while (loaded.length < total)

			return sortGroups(loaded)
		}
		catch (err) {
			logger.warning(`Phonebook: Could not load the phonebook groups: ${(err as Error).message}`)
			return undefined
		}
	}

	/**
	 * Add or replace a group, and give its contacts the (new) name in their search text
	 * @param group The group
	 */
	function putGroup(group: PbxPhonebookGroup) {
		groups.value = upsertGroup(groups.value, group)

		for (const contact of contacts.value) {
			if (contact.groups?.includes(group._id)) { buildSearch(contact) }
		}
	}

	/**
	 * Forget a deleted group and take it out of the local contacts - the API sends no PBX_PHONEBOOK_UPDATED for them
	 * @param id Id of the group
	 */
	function dropGroup(id: string) {
		groups.value = groups.value.filter(group => group._id !== id)

		for (const contact of stripGroup(contacts.value, id)) {
			buildSearch(contact)
		}
	}

	// #endregion

	// #region Event handling

	async function createdEvent(event: ConfigEventPbxPhonebookCreated) {
		if (!ss.isAuthenticated) { return }
		if (event.data.multiple && (event.data.personal === ss.auth?.employee || !event.data.personal)) { // multiple contacts created - either for user or shared
			loadContacts().then(() => { globalEvents.emit('Phonebook:Reloaded') })
			return
		}

		if (event.data.personal === ss.auth?.employee || !event.data.personal) { // single contact created - either for user or shared
			try {
				const res = await api.get<PbxPhonebookResponse>(`/pbx/phonebook/${event.id}`)

				makeSearchAndStuff(res.data)
				contacts.value.push(reactive(res.data))
				contacts.value.sort(dynamicSort('name'))
				globalEvents.emit('Phonebook:Created', res.data._id)
			}
			catch (_) { /** Ignore */ } // probably 404 not found or belonging to another employee and we have no access to it
		}
	}

	async function updatedEvent(event: ConfigEventPbxPhonebookUpdated) {
		if (!ss.isAuthenticated) { return }
		if (event.data.multiple && (event.data.personal === ss.auth?.employee || !event.data.personal)) { // multiple contacts updated - either for user or shared
			loadContacts().then(() => { globalEvents.emit('Phonebook:Reloaded') })
			return
		}

		if (event.data.personal === ss.auth?.employee || !event.data.personal) { // single contact updated - either for user or shared
			try {
				const res = await api.get<PbxPhonebookResponse>(`/pbx/phonebook/${event.id}`)

				makeSearchAndStuff(res.data)
				const index = contacts.value.findIndex(contact => contact._id === event.id)

				if (index !== -1) {
					contacts.value[index] = reactive(res.data)
					globalEvents.emit('Phonebook:Updated', res.data._id)
				} else {
					contacts.value.push(reactive(res.data))
					contacts.value.sort(dynamicSort('name'))
					globalEvents.emit('Phonebook:Created', res.data._id)
				}
			}
			catch (_) { /** Ignore */ } // probably 404 not found or belonging to another employee and we have no access to it
		}
	}

	function deletedEvent(event: ConfigEventCommon) {
		const index = contacts.value.findIndex(contact => contact._id === event.id)

		if (index !== -1) {
			contacts.value.splice(index, 1)
			globalEvents.emit('Phonebook:Deleted', event.id)
		}
	}

	function deleteAllEvent(event: ConfigEventPbxPhonebookDeleteAll) {
		if (!ss.isAuthenticated) { return }
		if (event.data.deleted === 'PERSONAL' && event.data.for === ss.auth?.employee) {
			contacts.value = contacts.value.filter(contact => contact.employee !== ss.auth?.employee)
		} else { // shared
			contacts.value = contacts.value.filter(contact => contact.employee != null)
		}

		globalEvents.emit('Phonebook:Reloaded')
	}

	async function groupChangedEvent(event: ConfigEventPbxPhonebookGroupCreated | ConfigEventPbxPhonebookGroupUpdated) {
		if (!ss.isAuthenticated) { return }
		if (event.data?.personal && event.data.personal !== ss.auth?.employee) { return } // another employee's personal group

		try {
			const res = await api.get<PbxPhonebookGroupResponse>(`/pbx/phonebookgroup/${event.id}`)

			putGroup(res.data)
		}
		catch (_) { /** Ignore */ } // probably deleted again already
	}

	function groupDeletedEvent(event: ConfigEventPbxPhonebookGroupDeleted) {
		if (!ss.isAuthenticated) { return }
		dropGroup(event.id)
	}

	// #endregion

	// #region Public methods

	/** Is phonebook ready */
	const isReady = computed(() => _ready.value)

	/** True when the groups have been loaded - until then a contact's groups must not be changed */
	const groupsLoaded = computed(() => _groupsLoaded.value)

	/** The customer groups - for shared contacts */
	const customerGroups = computed(() => groupsForKind(groups.value, true, ss.auth?.employee))

	/** The user's own personal groups - for the user's personal contacts */
	const myGroups = computed(() => groupsForKind(groups.value, false, ss.auth?.employee))

	/** Number of loaded contacts in each group, by group id */
	const groupCounts = computed(() => countGroups(contacts.value))

	/** May the user create, rename and delete the customer groups */
	const canManageCustomerGroups = computed(() => ss.accessLevelIsMin(EmployeeAccessLevel.MANAGER))

	/** Wait for the phonebook to be ready */
	async function ready() {
		if (ss.isAuthenticated && _ready.value) { return }

		while (!_ready.value) { await sleep(250) }
	}

	/**
	 * Create a new contact
	 * @param contact Contact to create
	 * @returns The created contact
	 * @throws Axios error
	 */
	async function create(contact: PbxPhonebookCreateRequest) {
		const res = await api.post<PbxPhonebookCreateResponse>('/pbx/phonebook', contact)

		return res.data
	}

	/**
	 * Update a contact
	 * @param contact Contact to update
	 * @returns The updated contact
	 * @throws Axios error
	 */
	async function update(contact: PbxPhonebookUpdateRequest & { _id: string }) {
		const res = await api.post<PbxPhonebookUpdateResponse>('/pbx/phonebook/' + contact._id, contact)

		return res.data
	}

	/**
	 * Delete a contact
	 * @param id Id of the contact to remove
	 * @returns True on success
	 * @throws Axios error
	 */
	async function remove(id: string) {
		const res = await api.del<PbxPhonebookDeleteResponse>(`/pbx/phonebook/${id}`)

		return res.data.success
	}

	/**
	 * The names of a contact's groups
	 * @param contact The contact
	 * @returns The names sorted - always resolved through the groups, so a rename shows at once
	 */
	function contactGroupNames(contact: PbxPhonebookContact) {
		return groupNames(contact.groups, groupsById.value)
	}

	/**
	 * Create a phonebook group
	 * @param name Name of the group, 1-50 characters
	 * @param personal True for one of the user's own groups, false for a customer group (MANAGER and up)
	 * @returns The created group
	 * @throws Axios error - 409 if the name is taken, 422 if it is empty or too long
	 */
	async function createGroup(name: string, personal: boolean) {
		const body: PbxPhonebookGroupCreateRequest = { personal, name }
		const res = await api.post<PbxPhonebookGroupCreateResponse>('/pbx/phonebookgroup', body)

		putGroup(res.data)
		return res.data
	}

	/**
	 * Rename a phonebook group
	 * @param id Id of the group
	 * @param name New name, 1-50 characters
	 * @returns The renamed group
	 * @throws Axios error - 409 if the name is taken, 422 if it is empty or too long
	 */
	async function renameGroup(id: string, name: string) {
		const body: PbxPhonebookGroupUpdateRequest = { name }
		const res = await api.post<PbxPhonebookGroupUpdateResponse>(`/pbx/phonebookgroup/${id}`, body)

		putGroup(res.data)
		return res.data
	}

	/**
	 * Delete a phonebook group - the API first takes it out of every contact in it
	 * @param id Id of the group
	 * @returns Number of contacts it was taken out of
	 * @throws Axios error
	 */
	async function removeGroup(id: string) {
		const res = await api.del<PbxPhonebookGroupDeleteResponse>(`/pbx/phonebookgroup/${id}`)

		dropGroup(id)
		return res.data.contacts
	}

	/**
	 * Search for contacts
	 * @param filter Search filter
	 * @param type Type of contacts to search for (PERSONAL, SHARED, ALL)
	 * @returns List of contacts that matches
	 */
	function search(filter: string, type: 'PERSONAL' | 'SHARED' | 'ALL' = 'ALL') {
		let res: PbxPhonebookContact[] = []

		if (filter) {
			const regexps = filter.toLowerCase().split(' ').map(o => o.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).filter(o => o).map(o => new RegExp(o))

			res = contacts.value.filter(contact => {
				return regexps.every(regexp => regexp.test(contact.search as string))
			})
		} else {
			res = contacts.value
		}
		if (type === 'PERSONAL') {
			res = res.filter(contact => contact.employee)
		} else if (type === 'SHARED') {
			res = res.filter(contact => !contact.employee)
		}
		return res.sort(dynamicSort('name'))
	}

	/**
	 * Find the name of a contact from a phone number
	 * @param number Phone number to lookup E.164 format
	 * @returns Name of the contact who has the number
	 */
	function findNameFromNumber(number: string) {
		const noPrefixNumber = ss.cutNationalPrefix(number)
		let matches = contacts.value.filter(contact => contact.phoneNumbers.some(phone => phone.number === number || phone.number === noPrefixNumber))

		if (matches.length === 0) { return undefined }
		if (matches.length === 1) { return matches[0]?.name }

		if (matches.some(contact => contact.employee)) {
			matches = matches.filter(contact => contact.employee)
		}
		if (matches.some(contact => contact.type === PbxPhonebookContactType.PERSON)) {
			matches = matches.filter(contact => contact.type === PbxPhonebookContactType.PERSON)
		}

		return matches[0]?.name
	}

	/**
	 * Find the picture of a contact from a phone number
	 * @param number Phone number to lookup E.164 format
	 * @returns Picture id of the contact who has the number, if available
	 */
	function findPictureFromNumber(number: string) {
		const noPrefixNumber = ss.cutNationalPrefix(number)
		let matches = contacts.value.filter(contact => contact.phoneNumbers.some(phone => phone.number === number || phone.number === noPrefixNumber))

		if (matches.length === 0) { return undefined }
		if (matches.length === 1) { return matches[0]?.picture }

		if (matches.some(contact => contact.employee)) {
			matches = matches.filter(contact => contact.employee)
		}
		if (matches.some(contact => contact.type === PbxPhonebookContactType.PERSON)) {
			matches = matches.filter(contact => contact.type === PbxPhonebookContactType.PERSON)
		}

		return matches[0]?.picture
	}

	/**
	 * Get the picture URL of a contact from a phone number
	 * @param number Phone number to lookup E.164 format
	 * @param width Width of the picture
	 * @param height Height of the picture
	 * @returns Picture URL of the contact who has the number, if available
	 */
	function getPictureUrlFromNumber(number: string, width = 300, height = 300) {
		const pictureId = findPictureFromNumber(number)
		if (!pictureId) { return }

		return `https://image.telecomx.dk/${pictureId}.png?width=${width}&height=${height}`
	}

	return {
		contacts, isReady, ready, create, update, remove, search, findNameFromNumber, findPictureFromNumber, getPictureUrlFromNumber,
		groups, groupsLoaded, groupsById, customerGroups, myGroups, groupCounts, canManageCustomerGroups, contactGroupNames, createGroup, renameGroup, removeGroup
	}
})
